import { faker } from '@faker-js/faker/locale/es';
import type { Product, ProductFamily } from './types';

interface FamilyData {
  models: string[];
  materials: string[];
  dimensionRange: { l: [number, number]; w: [number, number]; h: [number, number] };
  weightRange: [number, number];
  cycleRange: [number, number];
}

const FAMILIES_DATA: Record<ProductFamily, FamilyData> = {
  cabina: {
    models: ['Evolution 4.0', 'Synergy', 'Premium Glass', 'Compact', 'Panoramic'],
    materials: ['Acero inoxidable 304', 'Aluminio anodizado', 'Acero pintado epoxy'],
    dimensionRange: { l: [1200, 2200], w: [900, 1800], h: [2000, 2400] },
    weightRange: [120, 480],
    cycleRange: [1800, 4200],
  },
  contrapeso: {
    models: ['CP-500', 'CP-750', 'CP-1000', 'CP-1250'],
    materials: ['Hierro fundido', 'Acero laminado'],
    dimensionRange: { l: [800, 1200], w: [200, 400], h: [2000, 2800] },
    weightRange: [500, 1250],
    cycleRange: [600, 1200],
  },
  polea: {
    models: ['P-320-EN-81', 'P-400-ISO', 'P-480-ISO'],
    materials: ['Fundición GG25', 'Acero forjado'],
    dimensionRange: { l: [320, 480], w: [80, 120], h: [320, 480] },
    weightRange: [18, 42],
    cycleRange: [180, 420],
  },
  guia: {
    models: ['T70/A', 'T89/B', 'T127/B'],
    materials: ['Acero S235JR', 'Acero S355'],
    dimensionRange: { l: [3000, 5000], w: [70, 127], h: [70, 127] },
    weightRange: [40, 95],
    cycleRange: [240, 480],
  },
  motor: {
    models: ['MGV-40', 'MGV-60', 'MGV-90', 'MGV-120'],
    materials: ['Carcasa fundición', 'Carcasa aluminio'],
    dimensionRange: { l: [600, 900], w: [400, 600], h: [400, 600] },
    weightRange: [180, 420],
    cycleRange: [3600, 7200],
  },
  limitador: {
    models: ['LV-200', 'LV-240', 'LV-300'],
    materials: ['Acero zincado'],
    dimensionRange: { l: [240, 320], w: [180, 240], h: [180, 240] },
    weightRange: [8, 18],
    cycleRange: [120, 300],
  },
  pisadera: {
    models: ['PIS-800', 'PIS-900', 'PIS-1100'],
    materials: ['Aluminio extruido', 'Acero inoxidable'],
    dimensionRange: { l: [800, 1100], w: [180, 240], h: [40, 80] },
    weightRange: [4, 12],
    cycleRange: [60, 180],
  },
  botonera: {
    models: ['BT-COP-LCD', 'BT-COP-TFT', 'BT-COP-VOICE'],
    materials: ['Acero inoxidable cepillado', 'ABS'],
    dimensionRange: { l: [200, 320], w: [100, 160], h: [20, 40] },
    weightRange: [1, 3],
    cycleRange: [90, 240],
  },
};

export function generateProducts(count: number = 42): Product[] {
  const products: Product[] = [];
  const families = Object.keys(FAMILIES_DATA) as ProductFamily[];

  for (let i = 0; i < count; i++) {
    const family = families[i % families.length];
    const fam = FAMILIES_DATA[family];
    const model = faker.helpers.arrayElement(fam.models);
    const sku = `TK-${family.toUpperCase().slice(0, 3)}-${faker.string.alphanumeric({ length: 5, casing: 'upper' })}`;
    products.push({
      sku,
      family,
      description: `${family.charAt(0).toUpperCase()}${family.slice(1)} ${model}`,
      model,
      material: faker.helpers.arrayElement(fam.materials),
      weight: faker.number.float({ min: fam.weightRange[0], max: fam.weightRange[1], fractionDigits: 1 }),
      dimensions: {
        l: faker.number.int({ min: fam.dimensionRange.l[0], max: fam.dimensionRange.l[1] }),
        w: faker.number.int({ min: fam.dimensionRange.w[0], max: fam.dimensionRange.w[1] }),
        h: faker.number.int({ min: fam.dimensionRange.h[0], max: fam.dimensionRange.h[1] }),
      },
      bom: [],
      cycleTimeSeconds: faker.number.int({ min: fam.cycleRange[0], max: fam.cycleRange[1] }),
      defectRate: faker.number.float({ min: 0.002, max: 0.035, fractionDigits: 4 }),
      customer: 'TK Elevadores',
    });
  }

  // Cross-link BOMs: cabinas/contrapesos consume 1-3 simpler components
  const components = products.filter((p) => ['polea', 'limitador', 'pisadera', 'botonera'].includes(p.family));
  for (const p of products) {
    if (p.family === 'cabina' || p.family === 'contrapeso') {
      p.bom = faker.helpers
        .arrayElements(components, { min: 1, max: 3 })
        .map((c) => ({ sku: c.sku, quantity: faker.number.int({ min: 1, max: 4 }) }));
    }
  }

  return products;
}
